import { useState, type FormEvent } from "react";
import type { User } from "@if-tracking/shared";

import { api, ApiError } from "../api";

interface LoginProps {
  onLoggedIn: (user: User) => void | Promise<void>;
  onError: (message: string) => void;
}

export function Login({ onLoggedIn, onError }: LoginProps) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!username.trim() || !password) {
      onError("Enter your username and password.");
      return;
    }
    setBusy(true);
    try {
      const user = await api.login(username.trim(), password);
      setPassword("");
      await onLoggedIn(user);
    } catch (error) {
      if (error instanceof ApiError && error.status === 429) {
        onError("Too many failed attempts. Wait a few minutes and try again.");
      } else if (error instanceof ApiError && error.isAuth) {
        onError("Incorrect username or password.");
      } else {
        onError(error instanceof Error ? error.message : "Sign in failed.");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="centered">
      <form className="card login" onSubmit={handleSubmit}>
        <span className="brand-mark large">◴</span>
        <h1>Fasting Tracker</h1>
        <p className="muted">Sign in to your account.</p>
        <input
          type="text"
          autoComplete="username"
          placeholder="Username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          disabled={busy}
          autoFocus
        />
        <input
          type="password"
          autoComplete="current-password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={busy}
        />
        <button className="primary" type="submit" disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}
